import React, { useMemo, useEffect, useState } from "react";
import classnames from 'classnames';
import debounce from 'lodash.debounce';
import BoldIcon from "../icons/BoldIcon";
import ItalicIcon from "../icons/ItalicIcon";
import UnderlineIcon from "../icons/UnderlineIcon";
import StrikethroughIcon from "../icons/StrikethroughIcon";
import MonospaceIcon from "../icons/MonospaceIcon";
import LinkIcon from "../icons/LinkIcon";
import QuoteIcon from "../icons/QuoteIcon";
import { TextFormats } from "../util/global-variables";
import { isMobileView } from "../util/device";
import { findIntersection } from '../util/format';

const FormattingMenu = React.memo(function FormattingMenu({
  onOptionSelect,
  menuPosition,
  showClearAllBtn,
  hasPro,
  isTrialSubscription,
  messageInput,
  editableMessageRef,
  buttonClassName,
  buttonActiveClassName,
  clearClassName,
  entities,
  selection,
  menuMobileBottom,
  menuMobileLeft,
  menuMobileBorder,
  menuMobileShadow,
  menuShadow,
}) {
  const getIconColor = () => {
    if (!hasPro) {
      return 'text-[#E3E4E4] dark:text-[#48576D]';
    } else if (hasPro && isTrialSubscription) {
      return 'text-[#6E1C5C]';
    } else if (hasPro) {
      return 'text-[#323638] dark:text-white';
    }
  };

  const [keyboardHeight, setKeyboardHeight] = useState(0);

  const updateKeyboardHeight = () => {
    if (window.visualViewport) {
      const viewport = window.visualViewport;
      const layoutHeight = document.documentElement.clientHeight;
      const visualHeight = viewport.height;
      const newKeyboardHeight = layoutHeight - visualHeight;
      setKeyboardHeight(newKeyboardHeight > 0 ? newKeyboardHeight : 0);
    } else {
      setKeyboardHeight(0);
    }
  };

  useEffect(() => {
    updateKeyboardHeight();

    if (window.visualViewport) {
      const debouncedHandler = debounce(updateKeyboardHeight, 100);

      window.visualViewport.addEventListener('resize', debouncedHandler);
      window.visualViewport.addEventListener('scroll', debouncedHandler);

      return () => {
        window.visualViewport.removeEventListener('resize', debouncedHandler);
        window.visualViewport.removeEventListener('scroll', debouncedHandler);
        debouncedHandler.cancel();
      };
    }
  }, []);

  const menuItems = useMemo(() => {
    const items = [
      { name: TextFormats.BOLD, icon: <BoldIcon className="scale-110" />, className: classnames('w-7 h-7', buttonClassName) },
      { name: TextFormats.ITALIC, icon: <ItalicIcon className="scale-110" />, className: classnames('w-7 h-7', buttonClassName) },
      { name: TextFormats.UNDERLINE, icon: <UnderlineIcon className="scale-110" />, className: classnames('w-7 h-7', buttonClassName) },
      { name: TextFormats.STRIKETHROUGH, icon: <StrikethroughIcon className="scale-110" />, className: classnames('w-7 h-7', buttonClassName) },
      { name: TextFormats.CODE, icon: <MonospaceIcon className="scale-110" />, className: classnames('w-7 h-7', buttonClassName) },
      { name: TextFormats.TEXT_LINK, icon: <LinkIcon className="scale-110" />, className: classnames('w-7 h-7', buttonClassName) },
      { name: TextFormats.BLOCKQUOTE, icon: <QuoteIcon className="scale-110" />, className: classnames('w-7 h-7', buttonClassName) },
      {
        name: TextFormats.SPOILER,
        label: "Spoiler",
        className: classnames(`h-7 text-[11px] leading-[13.2px] ${getIconColor()} px-px`, buttonClassName),
      },
    ];

    if (showClearAllBtn) {
      items.unshift({
        name: TextFormats.CLEAR,
        label: "Clear All",
        className: classnames(
          `h-7 text-[11px] leading-[12.1px] mr-2 whitespace-nowrap hover:underline`,
          isTrialSubscription ? "text-[#6E1C5C]" : clearClassName ? clearClassName : "text-[#0E7CE0] dark:text-[#1C8EF5]",
        ),
      });
    }

    return items;
  }, [showClearAllBtn, hasPro, isTrialSubscription, buttonClassName, clearClassName]);

  const isMobile = isMobileView();
  const mobile = isMobile && messageInput;
  const bottomOffset = mobile ? (`${keyboardHeight + menuMobileBottom}px `) : 'auto';
  const menuStyle = useMemo(() => ({
    position: "fixed",
    top: mobile ? "auto" : `${menuPosition?.y - 50}px`,
    bottom: bottomOffset,
    left: mobile ? (menuMobileLeft || "0") : `${menuPosition?.x}px`,
    zIndex: 10,
    ...(mobile && { width: "100vw" }),
    overflowX: "auto",
  }), [mobile, menuPosition, menuMobileLeft, menuMobileBottom, bottomOffset]);

  return (
    <div
      className={classnames(
        'px-4 py-1.5 flex items-center space-x-1.5 max-w-[calc(100%+(1.5rem*2)+0.875rem+(0.75rem*2)+0.25rem)] md:max-w-[calc(100%+(2rem*2)+0.875rem+(0.75rem*2)+0.25rem)]',
        getIconColor(),
        isTrialSubscription ? 'bg-[#F7F2F7]' : 'bg-white dark:bg-[#242F3E]',
        !mobile && 'rounded-[100px]',
        (mobile ? menuMobileShadow : menuShadow) || 'shadow-[-2px_-1px_6px_0px_rgba(7,54,97,0.04)]',
        (mobile && menuMobileBorder)
      )}
      style={menuStyle}
      onMouseDown={(e) => e.preventDefault()} // Prevent focus shift when interacting with the menu
    >
      {menuItems.map((item, index) => (
        <div key={item.name}
          className={classnames(
            `cursor-pointer flex items-center`,
            item.className,
            entities.some(entity => (findIntersection(selection, entity, false) !== -1) && entity.type === item.name) && buttonActiveClassName,
          )}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();

            if (!hasPro && !isTrialSubscription && editableMessageRef?.current) {
              editableMessageRef.current.blur();
            }
            onOptionSelect(item.name);
          }}
        >
          {item.icon ? item.icon : item.label}
        </div>
      ))}
    </div>
  );
});

export default FormattingMenu;
